/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import GridLayout from 'react-grid-layout';
import { useSelector } from 'react-redux';
import { update } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import { getData, getId } from '../../utils/item';
import { evaluateConditionExpression } from '../../utils/num';
import { isEditable } from '../../utils/roles';
import { BoardGridMap } from './map/board.GridMap.js';
import { hasDynamicHeight } from '../../utils/gridLayout';
import { BoardDragProvider } from '../../context/boardDrag';
import { getInputCapabilities } from '../../utils/inputCapabilities';
import axios from 'axios';
import { saveMany } from '../../store/items';
import { useDispatch } from 'react-redux';

export const BoardGrid = props => {
  const { boardId, boardWidth, editable, layoutEditable = editable } = props;

  //   const item = useSelector(state => state.items[itemId]);
  const tab = useSelector(state => state.items[boardId]);
  const isMobile = useSelector(state => state.global.isMobile);
  const isTouch = useSelector(state => state.global.isTouch);
  const items = useSelector(state => state.items);
  const dispatch = useDispatch();


  const [layout, setLayout] = useState([]);
  const [tmpLayout, setTmpLayout] = useState([]);
  const [revealedWidgetKeys, setRevealedWidgetKeys] = useState(() => new Set());
  const revealedWidgetKeysRef = useRef(new Set());
  const lastRevealSignatureRef = useRef(null);
  const fullyRevealedBoardsRef = useRef(new Set());
  const rowH = isMobile ? 36 : 30;
  const inputCapabilities = useMemo(() => getInputCapabilities(), []);
  const isGridEditable = isEditable(
    layoutEditable && isMobile === false,
  );
  // Some Windows desktops expose touch capability even when users interact with
  // a mouse/trackpad. Keep the lock only for touch-only contexts.
  const touchLocked =
    (isTouch || inputCapabilities.hasTouchCapability) &&
    !inputCapabilities.hasDesktopPointer;
  const canEditGridInteractions = isGridEditable;
  const lastBatchKeyRef = useRef(null);
  const inflightBatchRef = useRef(new Set());
  const requestedBatchIdsRef = useRef(new Set());
  const isUserInteractingRef = useRef(false);
  const isValidObjectId = id => /^[a-f0-9]{24}$/i.test(id);
  const getLayoutKey = useCallback(
    element => `${boardId}:${element?.i ?? ''}:${element?.item ?? ''}`,
    [boardId],
  );

  useEffect(() => {
    revealedWidgetKeysRef.current = revealedWidgetKeys;
  }, [revealedWidgetKeys]);

  // On touch devices, accidental drags/resizes are very easy to trigger while scrolling.
  // We keep grid interactions for mouse, but block touch/pointer-touch from reaching RGL.
  const shouldBlockTouchGridInteraction = useCallback(target => {
    if (!target || typeof target.closest !== 'function') {
      return false;
    }
    // Allow any element explicitly marked as "no drag" (sliders, buttons, etc.)
    if (target.closest('.board-grid-no-drag')) {
      return false;
    }
    // Allow native form controls + links (should remain usable on touch).
    if (
      target.closest(
        'input, textarea, button, select, a, [contenteditable="true"]',
      )
    ) {
      return false;
    }
    // Block touch interactions inside grid items so they don't start drag/resize.
    return Boolean(target.closest('.react-grid-item'));
  }, []);

  const onTouchStartCapture = useCallback(
    e => {
      if (!touchLocked) {
        return;
      }
      if (shouldBlockTouchGridInteraction(e.target)) {
        e.stopPropagation();
      }
    },
    [shouldBlockTouchGridInteraction, touchLocked],
  );

  const onPointerDownCapture = useCallback(
    e => {
      if (!touchLocked) {
        return;
      }
      if (e.pointerType === 'touch' && shouldBlockTouchGridInteraction(e.target)) {
        e.stopPropagation();
      }
    },
    [shouldBlockTouchGridInteraction, touchLocked],
  );

  const shouldHideWidget = useCallback(
    (gridElement, elementId) => {
      const itemId = gridElement?.item;
      const targetItem = items?.[itemId];
      if (!targetItem?.conditions || targetItem.conditions.length === 0) {
        return false;
      }
      const normalizedKey = `${gridElement?.type || 'widget'}:${elementId}`;
      for (const conditionEntry of targetItem.conditions) {
        const conditionId = getId(conditionEntry);
        const condition = conditionEntry[conditionId];
        const stateKey = condition?.state || 'inactive';
        const stateData = condition?.[stateKey];
        if (
          !stateData ||
          !evaluateConditionExpression(stateData?.condition, targetItem, items)
        ) {
          continue;
        }
        const visibilityMap = stateData?.widgetsVisibility;
        if (!visibilityMap) {
          continue;
        }
        if (
          visibilityMap[elementId] === 'hidden' ||
          visibilityMap[normalizedKey] === 'hidden'
        ) {
          return true;
        }
      }
      return false;
    },
    [items],
  );

  const hiddenWidgetIds = useMemo(() => {
    const hidden = new Set();
    if (!Array.isArray(tab?.grid)) {
      return hidden;
    }
    for (const gridObj of tab.grid) {
      const elementId = getId(gridObj);
      const gridElement = gridObj[elementId];
      if (!gridElement) {
        continue;
      }
      if (shouldHideWidget(gridElement, elementId)) {
        hidden.add(elementId);
      }
    }
    return hidden;
  }, [tab, shouldHideWidget]);

  const initialVisibleWidgetKeys = useMemo(() => {
    const keys = new Set();
    if (!Array.isArray(tab?.grid)) {
      return keys;
    }
    const viewportHeight =
      typeof window !== 'undefined' ? window.innerHeight || 900 : 900;
    const rowsInView = Math.ceil(viewportHeight / rowH) + 8;
    for (const gridObj of tab.grid) {
      const elementId = getId(gridObj);
      if (hiddenWidgetIds.has(elementId)) {
        continue;
      }
      const element = gridObj?.[elementId];
      if (!element) {
        continue;
      }
      const y = Number.isFinite(element?.y) ? element.y : 0;
      if (y <= rowsInView) {
        keys.add(getLayoutKey(element));
      }
    }
    return keys;
  }, [tab?.grid, rowH, hiddenWidgetIds, getLayoutKey]);

  useEffect(() => {
    const allKeys = Array.isArray(layout)
      ? layout
          .map(element => element?.i)
          .filter(Boolean)
      : [];
    const layoutSignature = allKeys.join('|');

    if (allKeys.length === 0) {
      lastRevealSignatureRef.current = null;
      setRevealedWidgetKeys(new Set());
      return;
    }

    if (fullyRevealedBoardsRef.current.has(boardId)) {
      setRevealedWidgetKeys(new Set(allKeys));
      return;
    }

    if (lastRevealSignatureRef.current === layoutSignature) {
      return;
    }
    lastRevealSignatureRef.current = layoutSignature;

    const initialKeys = new Set();
    const alreadyRevealed = revealedWidgetKeysRef.current;
    allKeys.forEach(layoutKey => {
      if (alreadyRevealed.has(layoutKey) || initialVisibleWidgetKeys.has(layoutKey)) {
        initialKeys.add(layoutKey);
      }
    });
    if (initialKeys.size === 0) {
      initialKeys.add(allKeys[0]);
    }
    setRevealedWidgetKeys(initialKeys);

    const remainingKeys = allKeys.filter(layoutKey => !initialKeys.has(layoutKey));
    if (remainingKeys.length === 0) {
      return;
    }

    let cancelled = false;
    let timeoutId = null;
    let idleId = null;
    let index = 0;
    const chunkSize = 18;

    const revealNextChunk = () => {
      if (cancelled) {
        return;
      }
      setRevealedWidgetKeys(prev => {
        const next = new Set(prev);
        const end = Math.min(index + chunkSize, remainingKeys.length);
        for (let i = index; i < end; i++) {
          next.add(remainingKeys[i]);
        }
        index = end;
        if (index >= remainingKeys.length) {
          fullyRevealedBoardsRef.current.add(boardId);
        }
        return next;
      });
      if (index < remainingKeys.length) {
        scheduleNext();
      }
    };

    const scheduleNext = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => {
          revealNextChunk();
        });
      } else {
        timeoutId = window.setTimeout(() => {
          revealNextChunk();
        }, 24);
      }
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (
        idleId &&
        typeof window !== 'undefined' &&
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [layout, boardId, initialVisibleWidgetKeys]);

  const missingBatchIds = useMemo(() => {
    if (!Array.isArray(tab?.grid) || !tab?._id) {
      return [];
    }
    const idsToFetch = new Set();
    tab.grid.forEach(gridObj => {
      const elementId = getId(gridObj);
      const gridElement = gridObj[elementId];
      if (!gridElement) {
        return;
      }
      const ownerItemId = gridElement?.item;
      if (ownerItemId && isValidObjectId(ownerItemId)) {
        if (requestedBatchIdsRef.current.has(ownerItemId)) {
          return;
        }
        const existing = items?.[ownerItemId];
        if (!existing || existing.__partial) {
          idsToFetch.add(ownerItemId);
        }
      }
      if (gridElement?.type === 'link' && isValidObjectId(elementId)) {
        if (requestedBatchIdsRef.current.has(elementId)) {
          return;
        }
        const existing = items?.[elementId];
        if (!existing || existing.__partial) {
          idsToFetch.add(elementId);
        }
      }
      if (
        (gridElement?.type === 'timeline' ||
          gridElement?.type === 'timelines') &&
        ownerItemId &&
        items?.[ownerItemId]?.timelines
      ) {
        const timelineEntry = items[ownerItemId].timelines.find(
          entry => getId(entry) === elementId,
        );
        const timelineData = timelineEntry?.[elementId];
        if (timelineData?.entries) {
          timelineData.entries.forEach(entry => {
            if (entry?.itemId && isValidObjectId(entry.itemId)) {
              if (requestedBatchIdsRef.current.has(entry.itemId)) {
                return;
              }
              const existing = items?.[entry.itemId];
              if (!existing || existing.__partial) {
                idsToFetch.add(entry.itemId);
              }
            }
          });
        }
      }
    });
    return Array.from(idsToFetch).sort();
  }, [tab?.grid, tab?._id, items]);

  const missingBatchKey = useMemo(
    () => missingBatchIds.join(','),
    [missingBatchIds],
  );

  useEffect(() => {
    if (!tab?._id || missingBatchIds.length === 0) {
      return;
    }
    const batchKey = missingBatchKey;
    if (lastBatchKeyRef.current === batchKey) {
      return;
    }
    if (inflightBatchRef.current.has(batchKey)) {
      return;
    }
    lastBatchKeyRef.current = batchKey;
    inflightBatchRef.current.add(batchKey);
    missingBatchIds.forEach(id => requestedBatchIdsRef.current.add(id));

    const fetchBatch = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missingBatchIds },
        );
        if (Array.isArray(res.data) && res.data.length > 0) {
          dispatch(saveMany({ items: res.data, log: 'board-grid-batch' }));
        }
      } catch (e) {
        missingBatchIds.forEach(id => requestedBatchIdsRef.current.delete(id));
        // ignore
      } finally {
        inflightBatchRef.current.delete(batchKey);
      }
    };
    fetchBatch();
  }, [tab?._id, missingBatchKey, missingBatchIds, dispatch]);

  /**
   * Use to display the text in dynamic height
   * @param {*} height
   * @param {*} elementId
   * @param {*} itemId
   */
  const getTextHeight = useCallback(
    (height, elementId, itemId, reason, layoutKey) => {
      const paddingByReason = reason === 'filters' ? 10 : 60;
      const newHeight = Math.max(
        1,
        Math.round(((height + paddingByReason) / rowH) * 100) / 100,
      );
      let resizedElement = null;
      setLayout(prevLayout => {
        const clonedLayout = deepCloneArray(prevLayout);
        const idx = clonedLayout.findIndex(element => {
          if (layoutKey && element.i === layoutKey) {
            return true;
          }
          return element.i === elementId + itemId;
        });
        if (idx === -1) return prevLayout;
        if (!hasDynamicHeight(clonedLayout?.[idx])) return prevLayout;
        if (Math.abs((clonedLayout[idx].h ?? 0) - newHeight) < 0.05) {
          return prevLayout;
        }
        clonedLayout[idx].h = newHeight;
        clonedLayout[idx].isResizable = false;
        resizedElement = { ...clonedLayout[idx] };
        return clonedLayout;
      });
      if (!resizedElement) {
        return;
      }
      setTmpLayout(prev => {
        const existingIdx = prev.findIndex(
          entry =>
            entry?.i === resizedElement?.i && entry?.item === resizedElement?.item,
        );
        if (existingIdx === -1) {
          return [resizedElement, ...prev];
        }
        if (Math.abs((prev[existingIdx]?.h ?? 0) - resizedElement.h) < 0.05) {
          return prev;
        }
        const next = [...prev];
        next[existingIdx] = resizedElement;
        return next;
      });
    },
    [rowH],
  );

  useEffect(() => {
    let nbDynamics = 0;
    for (const element of layout) {
      if (hasDynamicHeight(element)) {
        nbDynamics++;
      }
    }
    if (nbDynamics <= tmpLayout.length && nbDynamics > 0) {
      const clonedLayout = deepCloneArray(layout);
      for (const element of clonedLayout) {
        for (const resizedElement of tmpLayout) {
          if (
            resizedElement.i === element.i &&
            resizedElement.item === element.item
          ) {
            element.h = resizedElement.h;
          }
        }
      }
      setTmpLayout([]);
      setLayout(clonedLayout);
    }
  }, [tmpLayout]);

  useEffect(() => {
    setLayout([]);
    setTmpLayout([]);
    setRevealedWidgetKeys(new Set());
    revealedWidgetKeysRef.current = new Set();
    lastRevealSignatureRef.current = null;
    lastBatchKeyRef.current = null;
    inflightBatchRef.current.clear();
    requestedBatchIdsRef.current.clear();
  }, [boardId]);

  useEffect(() => {
    if (!Array.isArray(tab?.grid) || tab._id !== boardId) {
      setLayout([]);
      return;
    }
    setLayout(prevLayout => {
      const prevHeightMap = new Map(
        prevLayout.map(entry => [entry.i, entry.h]),
      );
      const arr = [];
      for (const gridObj of tab.grid) {
        const elementId = getId(gridObj);
        if (hiddenWidgetIds.has(elementId)) {
          continue;
        }
        const gridElement = gridObj[elementId];
        if (!gridElement) {
          continue;
        }
        const layoutKey = getLayoutKey(gridElement);
        const prevHeight = prevHeightMap.get(layoutKey);
        const canSetH =
          hasDynamicHeight(gridElement) === false ||
          !Number.isFinite(prevHeight);
        const resizable =
          canEditGridInteractions && gridElement?.isResizable !== false;
        const draggable =
          canEditGridInteractions && gridElement?.isDraggable !== false;
        arr.push({
          ...gridElement,
          isResizable: resizable,
          isDraggable: draggable,
          h: canSetH ? gridElement.h : prevHeight,
          i: layoutKey,
        });
      }
      return arr;
    });
  }, [tab, boardId, hiddenWidgetIds, canEditGridInteractions, getLayoutKey]);

  return (
    <BoardDragProvider>
      <div>
        <div
          onTouchStartCapture={onTouchStartCapture}
          onPointerDownCapture={onPointerDownCapture}
          style={isTouch ? { touchAction: 'pan-y' } : undefined}
        >
          <GridLayout
            isDraggable={canEditGridInteractions}
            isResizable={canEditGridInteractions}
            margin={[0, 0]}
            className='layout'
            cols={12}
            rowHeight={rowH}
            width={boardWidth}
            compactType={'vertical'}
            layout={layout}
            draggableCancel={[
              '.board-grid-no-drag',
              'input',
              'textarea',
              'button',
              'select',
              'a',
              'label',
              '[contenteditable="true"]',
              '.MuiModal-root',
              '[data-modal-root="true"]',
            ].join(', ')}
            onDragStart={() => {
              isUserInteractingRef.current = true;
            }}
            onResizeStart={() => {
              isUserInteractingRef.current = true;
            }}
            onDragStop={() => {
              window.requestAnimationFrame(() => {
                isUserInteractingRef.current = false;
              });
            }}
            onResizeStop={() => {
              window.requestAnimationFrame(() => {
                isUserInteractingRef.current = false;
              });
            }}
            onLayoutChange={updatedLayout => {
              // Ignore programmatic/layout auto-reflow updates (dynamic-height, mount sync, etc.)
              // and only persist explicit user drag/resize interactions.
              if (!isUserInteractingRef.current) {
                return;
              }
              if (Array.isArray(tab?.grid) && tab._id === boardId) {
                const clonedGrid = deepCloneArray(tab?.grid);
                if (clonedGrid.length > 0) {
                  const keyToEntryRef = new Map();
                  clonedGrid.forEach((gridObj, idx) => {
                    const entryId = getId(gridObj);
                    const entry = gridObj?.[entryId];
                    if (!entry) {
                      return;
                    }
                    keyToEntryRef.set(getLayoutKey(entry), {
                      idx,
                      entryId,
                    });
                  });

                  for (const updatedElement of updatedLayout) {
                    const entryRef = keyToEntryRef.get(updatedElement.i);
                    if (!entryRef) {
                      continue;
                    }
                    const { idx, entryId } = entryRef;
                    const gridElement = clonedGrid?.[idx]?.[entryId];
                    if (!gridElement || gridElement?.x <= -1) {
                      continue;
                    }
                    gridElement.x = updatedElement.x;
                    gridElement.y = updatedElement.y;
                    gridElement.w = updatedElement.w;
                    if (hasDynamicHeight(gridElement) === false) {
                      gridElement.h = updatedElement.h;
                    }
                  }

                  if (JSON.stringify(clonedGrid) !== JSON.stringify(tab?.grid)) {
                    update(`items/${tab._id}`, 'grid', clonedGrid);
                  }
                }
              }
            }}
          >
            {Array.isArray(tab?.grid) &&
              tab.grid.map(element => {
                const elementId = getId(element);
                if (hiddenWidgetIds.has(elementId)) {
                  return null;
                }
                const elementObj = getData(element);
                const resizable =
                  canEditGridInteractions && elementObj?.isResizable !== false;
                const draggable =
                  canEditGridInteractions && elementObj?.isDraggable !== false;
                return (
                  <div
                    data-testid='board-grid-item'
                    data-grid={{
                      ...elementObj,
                      isResizable: resizable,
                      isDraggable: draggable,
                      i: getLayoutKey(elementObj),
                    }}
                    key={getLayoutKey(elementObj)}
                  >
                    {isGridEditable ||
                    (revealedWidgetKeys.size === 0 &&
                      initialVisibleWidgetKeys.has(
                        getLayoutKey(elementObj),
                      )) ||
                    revealedWidgetKeys.has(getLayoutKey(elementObj)) ? (
                        <BoardGridMap
                          tab={tab}
                          element={element}
                          editable={editable}
                          widgetEditable={editable}
                          getTextHeight={getTextHeight}
                        gridLayoutKey={getLayoutKey(elementObj)}
                        boardWidth={boardWidth}
                        rowHeight={rowH}
                        cols={12}
                      />
                    ) : (
                      <div className='w-full h-full bg-main bg-opacity-15 rounded' />
                    )}
                  </div>
                );
              })}
          </GridLayout>
        </div>
      </div>
    </BoardDragProvider>
  );
};
